import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Phone } from 'phoneNumberUtils';
import { FieldRenderProps } from 'react-final-form';
import { countriesCall, CountryCodes, findCallByCountry } from 'data/countries';
import Select, {
  OptionValue,
} from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'ncoded-component-library';
import VerifiedBadge from 'components/VerifiedBadge';
import { isEnterPressed } from 'utils';

import './PhoneField.styles.scss';

type PhoneFieldProps = {
  className?: string;
  verified?: boolean;
  onVerification?: () => void;
} & FieldRenderProps<Phone, HTMLElement>;

const PhoneField: React.FC<PhoneFieldProps> = (props) => {
  const {
    className,
    disabled,
    required,
    input: { value, onChange, ...restInput },
    meta: { error, touched },
    verified = false,
    onVerification,
    ...rest
  } = props;

  const classes = classNames('phone-field', className);

  const hasError = useMemo(() => error && touched, [error, touched]);

  const { t } = useTranslation();

  const suffixNode = useMemo(() => {
    if (verified || !onVerification) return null;

    return (
      <Button
        className="verify"
        variant="outline"
        disabled={!!error || !value?.phoneNumber || disabled}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onVerification?.();
        }}
      >
        {t('verify')}
      </Button>
    );
  }, [verified, onVerification, error, value?.phoneNumber, disabled, t]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const phoneNumber = event.target.value;

      onChange({
        ...value,
        phoneNumber,
      });
    },
    [onChange, value],
  );

  const handleOnCountryCall = useCallback(
    (option: OptionValue<CountryCodes>) => {
      onChange({
        ...value,
        country: option.value,
        countryCode: findCallByCountry(option.value),
      });
    },
    [value, onChange],
  );

  return (
    <div
      className={classes}
      onKeyDown={(e) => {
        if (isEnterPressed(e)) {
          e.preventDefault();
        }
      }}
    >
      <div className="phone-field__container">
        <div className="phone-field__container__country">
          <label className={classNames({ required })}>{t('country')}</label>

          <Select
            className="country-code"
            searchable
            hasError={hasError}
            options={countriesCall}
            value={value.country}
            disabled={disabled}
            onChange={handleOnCountryCall}
            renderAsPortal
            portalClassName="cn-sel-port"
          />
        </div>

        <div className="phone-field__container__phone">
          <label className={classNames({ required })}>{t('phoneNumber')}</label>

          <div
            className="phone-field__container__phone__input-container"
            onKeyDown={(e) => {
              if (!verified && isEnterPressed(e)) onVerification?.();
            }}
          >
            <Input
              {...rest}
              {...restInput}
              type="tel"
              hasError={hasError}
              disabled={disabled}
              className="phone-field__container__phone__input-container__phone-number"
              placeholder={t('phoneNumber')}
              value={value.phoneNumber}
              onChange={handleOnChange}
              suffixNode={suffixNode}
            />
            {verified && <VerifiedBadge text={t('verified')} />}
          </div>
        </div>
      </div>

      {hasError && <p className="phone-field__error">{error}</p>}
    </div>
  );
};

export default PhoneField;
